// ═══════════════════════════════════════════════════════════════
// ICED ESPRESSO - ELEGANT ANIMATION ENGINE
// ═══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// PARTICLE SYSTEM - Subtle floating particles (40 total)
// ══════════════════════════════════════════════════════════════

class ParticleSystem {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'particle-canvas';
    document.body.prepend(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    this.particles = [];
    this.mouse = { x: 0, y: 0 };
    this.resize();
    this.init();
    this.animate();
    
    window.addEventListener('resize', () => this.resize());
    document.addEventListener('mousemove', (e) => {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    });
  }
  
  resize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }
  
  init() {
    // Subtle floating particles - 40 total
    for (let i = 0; i < 40; i++) {
      this.particles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        size: Math.random() * 2 + 0.5,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.4 + 0.1,
        pulse: Math.random() * Math.PI * 2,
        color: Math.random() > 0.8 ? '#d4eeff' : '#c9a84c'
      });
    }
  }
  
  drawParticle(p) {
    this.ctx.save();
    this.ctx.globalAlpha = p.opacity * (0.6 + Math.sin(p.pulse) * 0.2);
    
    const gradient = this.ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 2);
    gradient.addColorStop(0, p.color);
    gradient.addColorStop(1, 'transparent');
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
    this.ctx.fill();
    
    this.ctx.restore();
  }
  
  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    this.particles.forEach(p => {
      // Gentle mouse interaction
      const dx = this.mouse.x - p.x;
      const dy = this.mouse.y - p.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist < 100) {
        const force = (100 - dist) / 100;
        p.x -= dx * force * 0.01;
        p.y -= dy * force * 0.01;
      }
      
      // Update position
      p.x += p.speedX;
      p.y += p.speedY;
      p.pulse += 0.01;
      
      // Wrap around edges
      if (p.x < 0) p.x = this.canvas.width;
      if (p.x > this.canvas.width) p.x = 0;
      if (p.y < 0) p.y = this.canvas.height;
      if (p.y > this.canvas.height) p.y = 0;
      
      this.drawParticle(p);
    });
    
    requestAnimationFrame(() => this.animate());
  }
}

// ══════════════════════════════════════════════════════════════
// CUSTOM CURSOR - Simple and elegant
// ══════════════════════════════════════════════════════════════

class CustomCursor {
  constructor() {
    this.cursor = document.getElementById('cursor');
    this.ring = document.getElementById('cursorRing');
    if (!this.cursor || !this.ring) return;
    
    this.mx = 0;
    this.my = 0;
    this.rx = 0;
    this.ry = 0;
    
    this.init();
  }
  
  init() {
    document.addEventListener('mousemove', (e) => this.onMouseMove(e));
    document.addEventListener('mousedown', () => this.onClick());
    document.addEventListener('mouseup', () => this.onRelease());
    
    // Hover effects
    document.querySelectorAll('a, button, .product-card, .btn-primary, .btn-secondary, .nav-cta').forEach(el => {
      el.addEventListener('mouseenter', () => this.onHover());
      el.addEventListener('mouseleave', () => this.onLeave());
    });
    
    this.animateRing();
  }
  
  onMouseMove(e) {
    this.mx = e.clientX;
    this.my = e.clientY;
    this.cursor.style.left = this.mx - 6 + 'px';
    this.cursor.style.top = this.my - 6 + 'px';
  }
  
  onClick() {
    this.cursor.classList.add('clicking');
  }
  
  onRelease() {
    this.cursor.classList.remove('clicking');
  }
  
  onHover() {
    this.cursor.style.transform = 'scale(1.5)';
    this.ring.classList.add('hovering');
  }
  
  onLeave() {
    this.cursor.style.transform = 'scale(1)';
    this.ring.classList.remove('hovering');
  }
  
  animateRing() {
    this.rx += (this.mx - this.rx - 18) * 0.15;
    this.ry += (this.my - this.ry - 18) * 0.15;
    this.ring.style.left = this.rx + 'px';
    this.ring.style.top = this.ry + 'px';
    requestAnimationFrame(() => this.animateRing());
  }
}

// ══════════════════════════════════════════════════════════════
// SCROLL ANIMATIONS - Simple reveal on scroll
// ══════════════════════════════════════════════════════════════

class ScrollAnimations {
  constructor() {
    this.initObserver();
    this.initScrollProgress();
  }
  
  initObserver() {
    const options = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, options);
    
    // Observe animatable elements
    const animatables = document.querySelectorAll(
      '.product-card, .feature, .testimonial, .section-title, .section-label, .about-title'
    );
    
    animatables.forEach(el => observer.observe(el));
  }
  
  initScrollProgress() {
    const progressBar = document.createElement('div');
    progressBar.className = 'scroll-progress';
    document.body.appendChild(progressBar);
    
    window.addEventListener('scroll', () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      progressBar.style.width = progress + '%';
    });
  }
}

// ══════════════════════════════════════════════════════════════
// MAGNETIC BUTTONS
// ══════════════════════════════════════════════════════════════

class MagneticElements {
  constructor() {
    this.elements = document.querySelectorAll('.btn-primary, .btn-secondary, .btn-dark, .nav-cta');
    this.init();
  }
  
  init() {
    this.elements.forEach(el => {
      el.classList.add('magnetic');
      
      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        
        el.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
      });
      
      el.addEventListener('mouseleave', () => {
        el.style.transform = 'translate(0, 0)';
      });
    });
  }
}

// ══════════════════════════════════════════════════════════════
// TILT EFFECT ON CARDS
// ══════════════════════════════════════════════════════════════

class TiltEffect {
  constructor() {
    this.cards = document.querySelectorAll('.product-card, .testimonial, .feature');
    this.init();
  }
  
  init() {
    this.cards.forEach(card => {
      card.classList.add('tilt-card');
      
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const rotateX = (y - centerY) / 20;
        const rotateY = (centerX - x) / 20;
        
        card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
      });
      
      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale(1)';
      });
    });
  }
}

// ══════════════════════════════════════════════════════════════
// EASTER EGGS
// ══════════════════════════════════════════════════════════════

class EasterEggs {
  constructor() {
    this.konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    this.konamiIndex = 0;
    this.clickCount = 0;
    this.lastClickTime = 0;
    
    this.init();
  }
  
  init() {
    // Konami Code Easter Egg
    document.addEventListener('keydown', (e) => {
      if (e.key === this.konamiCode[this.konamiIndex]) {
        this.konamiIndex++;
        if (this.konamiIndex === this.konamiCode.length) {
          this.triggerKonami();
          this.konamiIndex = 0;
        }
      } else {
        this.konamiIndex = 0;
      }
    });
    
    // Logo triple-click Easter Egg
    const logo = document.querySelector('.nav-logo');
    if (logo) {
      logo.addEventListener('click', (e) => {
        const now = Date.now();
        if (now - this.lastClickTime < 500) {
          this.clickCount++;
          if (this.clickCount >= 3) {
            e.preventDefault();
            this.triggerLogoEasterEgg();
            this.clickCount = 0;
          }
        } else {
          this.clickCount = 1;
        }
        this.lastClickTime = now;
      });
    }
    
    // Secret hover on coffee emoji
    document.querySelectorAll('.nav-logo span, .about-emoji').forEach(el => {
      el.addEventListener('mouseenter', () => {
        this.coffeeHover(el);
      });
    });
  }
  
  triggerKonami() {
    // Confetti explosion!
    this.createConfetti(100);
    
    // Flash message
    const msg = document.createElement('div');
    msg.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-family: 'Bebas Neue', sans-serif;
      font-size: 60px;
      color: #c9a84c;
      z-index: 10002;
      text-shadow: 0 0 30px #c9a84c;
      animation: fadeIn 0.5s ease, fadeOut 0.5s 2s ease forwards;
    `;
    msg.textContent = '☕ LEGENDARY DRIP UNLOCKED! 💎';
    document.body.appendChild(msg);
    
    // Rainbow effect
    document.body.style.animation = 'rainbow-bg 2s ease-in-out';
    
    setTimeout(() => {
      msg.remove();
      document.body.style.animation = '';
    }, 3000);
  }
  
  triggerLogoEasterEgg() {
    // Spin all product cards
    const cards = document.querySelectorAll('.product-card');
    if (cards.length === 0) return;
    
    cards.forEach((card, i) => {
      if (card && card.isConnected) {
        card.style.animation = `spin360 0.8s ${i * 0.1}s ease-out`;
        setTimeout(() => {
          if (card && card.isConnected) {
            card.style.animation = '';
          }
        }, 1000 + i * 100);
      }
    });
    
    // Add spin animation if not exists
    if (!document.getElementById('spin-style')) {
      const style = document.createElement('style');
      style.id = 'spin-style';
      style.textContent = `
        @keyframes spin360 {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
        @keyframes fadeOut {
          to { opacity: 0; }
        }
        @keyframes rainbow-bg {
          0% { filter: hue-rotate(0deg); }
          50% { filter: hue-rotate(180deg); }
          100% { filter: hue-rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  coffeeHover(el) {
    // Steam particles rising from coffee
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        const steam = document.createElement('div');
        steam.style.cssText = `
          position: fixed;
          left: ${el.getBoundingClientRect().left + el.offsetWidth / 2}px;
          top: ${el.getBoundingClientRect().top}px;
          font-size: 16px;
          pointer-events: none;
          z-index: 10000;
          animation: steam-rise 1.5s ease-out forwards;
        `;
        steam.textContent = '☁️';
        document.body.appendChild(steam);
        
        setTimeout(() => steam.remove(), 1500);
      }, i * 150);
    }
    
    // Add steam animation
    if (!document.getElementById('steam-style')) {
      const style = document.createElement('style');
      style.id = 'steam-style';
      style.textContent = `
        @keyframes steam-rise {
          0% { opacity: 0.8; transform: translateY(0) scale(0.5); }
          100% { opacity: 0; transform: translateY(-60px) scale(1.5) rotate(20deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }
  
  createConfetti(count) {
    const colors = ['#c9a84c', '#e8cc7a', '#d4eeff', '#f5e6d0', '#ffffff'];
    
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + 'vw';
        confetti.style.top = '-10px';
        confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
        confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
        
        // Random shapes
        if (Math.random() > 0.5) {
          confetti.style.borderRadius = '50%';
        } else if (Math.random() > 0.5) {
          confetti.style.width = '5px';
          confetti.style.height = '15px';
        }
        
        document.body.appendChild(confetti);
        setTimeout(() => confetti.remove(), 4000);
      }, i * 20);
    }
  }
}

// ══════════════════════════════════════════════════════════════
// TEXT SCRAMBLE EFFECT (ENHANCED)
// ══════════════════════════════════════════════════════════════

class TextScramble {
  constructor() {
    this.chars = '!<>-_\\/[]{}—=+*^?#◆◇★☆♦♢✦✧●○';
    this.init();
  }
  
  init() {
    const titles = document.querySelectorAll('.section-title, .about-title, .cta-title');
    
    titles.forEach(title => {
      title.addEventListener('mouseenter', () => {
        this.scramble(title);
      });
    });
    
    // Auto scramble section titles periodically
    const sectionTitles = document.querySelectorAll('.section-title, .about-title');
    if (sectionTitles.length) {
      setInterval(() => {
        if (Math.random() > 0.7) {
          const randomTitle = sectionTitles[Math.floor(Math.random() * sectionTitles.length)];
          this.scramble(randomTitle);
        }
      }, 5000);
    }
  }
  
  scramble(el) {
    const originalText = el.dataset.original || el.textContent;
    el.dataset.original = originalText;
    
    let iteration = 0;
    const maxIterations = 15;
    
    const interval = setInterval(() => {
      el.textContent = originalText
        .split('')
        .map((char, index) => {
          if (index < iteration) {
            return originalText[index];
          }
          if (char === ' ' || char === '\n') return char;
          return this.chars[Math.floor(Math.random() * this.chars.length)];
        })
        .join('');
      
      iteration += 1/2;
      
      if (iteration >= originalText.length) {
        clearInterval(interval);
        el.textContent = originalText;
      }
    }, 25);
  }
}

// ══════════════════════════════════════════════════════════════
// RIPPLE EFFECT ON CLICK
// ══════════════════════════════════════════════════════════════

class RippleEffect {
  constructor() {
    this.init();
  }
  
  init() {
    document.querySelectorAll('.btn-primary, .btn-secondary, .btn-dark, .product-card').forEach(el => {
      el.addEventListener('click', (e) => this.createRipple(e, el));
    });
  }
  
  createRipple(e, el) {
    const rect = el.getBoundingClientRect();
    const ripple = document.createElement('div');
    const size = Math.max(rect.width, rect.height);
    
    ripple.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      left: ${e.clientX - rect.left - size/2}px;
      top: ${e.clientY - rect.top - size/2}px;
      background: radial-gradient(circle, rgba(201,168,76,0.4) 0%, transparent 70%);
      border-radius: 50%;
      transform: scale(0);
      animation: ripple-expand 0.6s ease-out forwards;
      pointer-events: none;
    `;
    
    el.style.position = 'relative';
    el.style.overflow = 'hidden';
    el.appendChild(ripple);
    
    setTimeout(() => ripple.remove(), 600);
    
    // Add ripple animation
    if (!document.getElementById('ripple-style')) {
      const style = document.createElement('style');
      style.id = 'ripple-style';
      style.textContent = `
        @keyframes ripple-expand {
          to { transform: scale(4); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// ══════════════════════════════════════════════════════════════
// SECTION REVEAL ANIMATIONS
// ══════════════════════════════════════════════════════════════

class SectionReveals {
  constructor() {
    this.init();
  }
  
  init() {
    // Add morphing background shapes to sections
    document.querySelectorAll('.section, .about, .testimonials').forEach(section => {
      const morph = document.createElement('div');
      morph.className = 'morph-bg';
      morph.style.cssText = `
        position: absolute;
        width: ${200 + Math.random() * 200}px;
        height: ${200 + Math.random() * 200}px;
        right: ${Math.random() * 20}%;
        top: ${Math.random() * 50}%;
        pointer-events: none;
      `;
      section.style.position = 'relative';
      section.style.overflow = 'hidden';
      section.appendChild(morph);
    });
  }
}

// ══════════════════════════════════════════════════════════════
// FLOATING DIAMONDS - Subtle ambient effect (6 total)
// ══════════════════════════════════════════════════════════════

class FloatingDiamonds {
  constructor() {
    this.createDiamonds();
  }
  
  createDiamonds() {
    const symbols = ['💎', '✨', '☕'];
    
    // Just 6 subtle floating elements
    for (let i = 0; i < 6; i++) {
      const diamond = document.createElement('div');
      diamond.className = 'floating-diamond';
      diamond.textContent = symbols[Math.floor(Math.random() * symbols.length)];
      diamond.style.left = Math.random() * 100 + 'vw';
      diamond.style.fontSize = (14 + Math.random() * 10) + 'px';
      diamond.style.animationDelay = Math.random() * 20 + 's';
      diamond.style.animationDuration = (20 + Math.random() * 15) + 's';
      diamond.style.opacity = 0.08 + Math.random() * 0.07;
      document.body.appendChild(diamond);
    }
  }
}

// ══════════════════════════════════════════════════════════════
// SMOOTH SCROLL
// ══════════════════════════════════════════════════════════════

class SmoothScroll {
  constructor() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(anchor.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }
}

// ══════════════════════════════════════════════════════════════
// ADD TO CART ANIMATION
// ══════════════════════════════════════════════════════════════

class CartAnimation {
  constructor() {
    this.init();
  }
  
  init() {
    document.querySelectorAll('.btn-add-cart, .product-add').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.animateAddToCart(e.target);
      });
    });
  }
  
  animateAddToCart(btn) {
    const original = btn.textContent || btn.querySelector('span')?.textContent;
    const cart = document.querySelector('.nav-cart');
    
    // Create flying item
    const flyingItem = document.createElement('div');
    flyingItem.style.cssText = `
      position: fixed;
      left: ${btn.getBoundingClientRect().left}px;
      top: ${btn.getBoundingClientRect().top}px;
      font-size: 24px;
      z-index: 10000;
      pointer-events: none;
      transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    `;
    flyingItem.textContent = '💎';
    document.body.appendChild(flyingItem);
    
    // Animate to cart
    setTimeout(() => {
      if (cart) {
        flyingItem.style.left = cart.getBoundingClientRect().left + 'px';
        flyingItem.style.top = cart.getBoundingClientRect().top + 'px';
        flyingItem.style.transform = 'scale(0)';
        flyingItem.style.opacity = '0';
      }
    }, 50);
    
    // Update button
    if (btn.querySelector('span')) {
      btn.querySelector('span').textContent = '✓ Added!';
    } else {
      btn.textContent = '✓';
    }
    btn.style.background = '#2a6e2a';
    
    // Shake cart
    if (cart) {
      setTimeout(() => {
        cart.style.animation = 'cart-shake 0.5s ease';
        setTimeout(() => cart.style.animation = '', 500);
      }, 700);
    }
    
    // Reset
    setTimeout(() => {
      flyingItem.remove();
      if (btn.querySelector('span')) {
        btn.querySelector('span').textContent = original;
      } else {
        btn.textContent = '+';
      }
      btn.style.background = '';
    }, 2000);
    
    // Add cart shake animation
    if (!document.getElementById('cart-shake-style')) {
      const style = document.createElement('style');
      style.id = 'cart-shake-style';
      style.textContent = `
        @keyframes cart-shake {
          0%, 100% { transform: translateX(0) rotate(0); }
          25% { transform: translateX(-5px) rotate(-5deg); }
          75% { transform: translateX(5px) rotate(5deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// ══════════════════════════════════════════════════════════════
// PAGE LOADER
// ══════════════════════════════════════════════════════════════

class PageLoader {
  constructor() {
    this.createLoader();
  }
  
  createLoader() {
    const loader = document.createElement('div');
    loader.className = 'page-loader';
    loader.id = 'page-loader';
    loader.innerHTML = `<div class="loader-logo">ICED☕ESPRESSO</div>`;
    document.body.prepend(loader);
    
    // Force remove after animation completes
    setTimeout(() => {
      loader.style.opacity = '0';
      loader.style.pointerEvents = 'none';
      loader.style.visibility = 'hidden';
    }, 2000);
    
    // Completely remove from DOM after fade
    setTimeout(() => {
      if (loader && loader.parentNode) {
        loader.parentNode.removeChild(loader);
      }
    }, 2500);
  }
}

// ══════════════════════════════════════════════════════════════
// SABRINA CARPENTER EASTER EGGS
// ══════════════════════════════════════════════════════════════

class SabrinaEasterEggs {
  constructor() {
    this.lyrics = [
      "that's that me espresso",
      "now he's thinkin' bout me every minute",
      "I know I mountain dew it for ya",
      "say you can't sleep, baby I know",
      "is it the shoes? is it the smile?",
      "boy, you're like my favorite coffee"
    ];
    this.ctaQuotes = [
      "~ now he's thinkin' bout me every minute ~",
      "~ that's that me espresso ~",
      "~ is it the drip? is it the shine? ~",
      "~ say you can't sleep, baby I know ~"
    ];
    this.ctaIndex = 0;
    this.init();
  }

  init() {
    // Easter Egg 2: Click ESPRESSO word to show floating lyrics
    const espWord = document.querySelector('.esp-word');
    if (espWord) {
      espWord.addEventListener('click', () => this.triggerLyric());
    }

    // Easter Egg 3: Rotate CTA quotes
    this.rotateCta();
  }

  triggerLyric() {
    const lyricEl = document.getElementById('sabrinaLyric');
    const espWord = document.querySelector('.esp-word');
    if (!lyricEl) return;

    const randomLyric = this.lyrics[Math.floor(Math.random() * this.lyrics.length)];
    
    // Flash the ESPRESSO word
    if (espWord) {
      espWord.classList.add('sabrina-click');
      setTimeout(() => espWord.classList.remove('sabrina-click'), 600);
    }

    // Type out the lyric
    lyricEl.classList.remove('show');
    lyricEl.textContent = '';
    
    setTimeout(() => {
      lyricEl.textContent = randomLyric;
      lyricEl.classList.add('show');
    }, 100);

    // Fade out after a few seconds
    setTimeout(() => {
      lyricEl.classList.remove('show');
    }, 3500);
  }

  rotateCta() {
    const ctaEl = document.getElementById('sabrinaCta');
    if (!ctaEl) return;

    // Enable pulsing after initial reveal
    setTimeout(() => ctaEl.classList.add('pulsing'), 2000);

    setInterval(() => {
      ctaEl.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
      ctaEl.style.opacity = '0';
      ctaEl.style.transform = 'translateX(20px)';
      
      setTimeout(() => {
        this.ctaIndex = (this.ctaIndex + 1) % this.ctaQuotes.length;
        ctaEl.textContent = this.ctaQuotes[this.ctaIndex];
        ctaEl.style.opacity = '';
        ctaEl.style.transform = '';
      }, 500);
    }, 6000);
  }
}

// ══════════════════════════════════════════════════════════════
// INITIALIZE
// ══════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', () => {
  // Core animations
  new PageLoader();
  new ParticleSystem();
  new CustomCursor();
  new ScrollAnimations();
  
  // Interactions
  new MagneticElements();
  new TiltEffect();
  new SmoothScroll();
  
  // Ambient effects
  new FloatingDiamonds();
  
  // Easter eggs
  new EasterEggs();
  new SabrinaEasterEggs();
  
  console.log('%c☕ ICED ESPRESSO ☕', 'font-size: 20px; color: #c9a84c;');
  console.log('%c♪ that\'s that me espresso ♪', 'font-size: 14px; color: #d4eeff; font-style: italic;');
});
